import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import { fileSrc } from "../lib/apiClient";
import {
  X, Play, RotateCcw, Sparkles, MousePointer2, Search, Send, CalendarClock,
  ListChecks, Plus, Check, ArrowRight, Bell, Video, Square,
} from "lucide-react";
import { useReelRecorder } from "./useReelRecorder";

const IMG = "https://static.prod-images.emergentagent.com/jobs/5d950d92-6b67-4808-826a-961251c2afb8/images/";
const PHOTOS = {
  living: IMG + "317fb7225f92575b95c7689bd98ad66085d74561aec1cbf7f4bb361f57980f4c.png",
  newEngland: IMG + "bbe5226141376f60f9159d28030d125945975ced6b352ac70b25a175085f3762.png",
  bedroom: IMG + "49282ca7f782ced793b1b128d18d13f160c4abeac26820d53849a0127cfe244c.png",
  sconce: IMG + "18cc864e25b03ca813eb79565b6dd328f7884e9d07c5b5af7b5dc5c2ebf092d2.png",
  home: IMG + "d0bf7caf38b06ca6b93a237cc8e366b2e3e5c8fc7834a2ac20fdd66cc9da5477.png",
  dining: IMG + "d3d07cede007f737b124e0c341652723bed3613141ebb4ff80db4f2e910f36bc.png",
};

const SCENES = [
  { caption: "Your studio dashboard", cursor: { x: 22, y: 62 }, narration: "Welcome to WorkRoom, the all in one studio platform for interior designers. Let's open the New Canaan coastal remodel." },
  { caption: "New Canaan · Coastal Remodel", cursor: { x: 50, y: 40 }, narration: "Every project, beautifully organized. New Canaan is now in the procurement phase, with checklists on track." },
  { caption: "Mood Boards · add from Pinterest", cursor: { x: 30, y: 24 }, narration: "Build mood boards in seconds. Pull inspiration straight from Pinterest, like this New England living room." },
  { caption: "Generate a paint palette", cursor: { x: 70, y: 22 }, narration: "Then generate a curated paint palette from your imagery, with a single click." },
  { caption: "FF&E · smart URL fetch", cursor: { x: 78, y: 30 }, narration: "Add furnishings instantly. Smart fetch a product link. Four brass sconces with cream shades, priced and specified automatically." },
  { caption: "Calendar · tasks & milestones", cursor: { x: 30, y: 78 }, narration: "Schedule and assign tasks, and keep every upcoming milestone in view." },
  { caption: "Notes · message the whole team", cursor: { x: 68, y: 80 }, narration: "Message your entire team and your client at once. Here, a budget meeting and a progress update." },
  { caption: "Budget allocations", cursor: { x: 55, y: 55 }, narration: "Manage your budget and allocations with total clarity." },
  { caption: "AI design assistant", cursor: { x: 74, y: 68 }, narration: "And let the AI design assistant do the legwork, finding estate sales and flea markets for that antique dining set. Then save it straight to your notes." },
  { caption: "", cursor: { x: 50, y: 50 }, narration: "WorkRoom. Design, managed beautifully." },
];

const REVEAL_MS = 2100;
const FALLBACK_MS = 8500;

/* ---------- shared bits ---------- */
const Frame = ({ children, tab }) => (
  <div className="absolute inset-0 flex flex-col bg-[#F9F8F6]">
    <div className="h-14 shrink-0 bg-white/95 border-b border-[#EBE5E0] flex items-center justify-between px-8">
      <span className="font-serif text-2xl text-[#2B2622] tracking-wide">WorkRoom</span>
      <div className="flex items-center gap-4">
        <span className="text-[11px] uppercase tracking-wider text-[#5C5651]">Preview as client</span>
        <span className="text-[11px] uppercase tracking-wider text-[#5C5651]">Settings</span>
        <div className="text-right">
          <p className="text-sm text-[#2B2622] font-medium leading-tight">Elise Sinclair</p>
          <p className="text-[10px] uppercase tracking-wider text-[#A68A6B]">designer</p>
        </div>
        <div className="w-8 h-8 bg-[#A68A6B] text-white flex items-center justify-center font-serif">E</div>
      </div>
    </div>
    {tab && (
      <div className="shrink-0 bg-white border-b border-[#EBE5E0] px-8 flex flex-wrap gap-x-4 gap-y-0.5 py-1 overflow-hidden">
        {["Overview", "Budget", "Approvals", "FF&E", "Purchase Orders", "Invoices", "Proposals", "Checklists / Assignments", "Time Tracking", "Documents", "Mood Boards", "Design Scheme", "Notes", "Calendar", "Photos", "Product Library", "AI Assistant"].map((t) => (
          <span key={t} className={`py-2 text-[10px] uppercase tracking-wider whitespace-nowrap border-b-2 ${t === tab ? "border-[#A68A6B] text-[#2B2622]" : "border-transparent text-[#9b938b]"}`}>{t}</span>
        ))}
      </div>
    )}
    <div className="flex-1 overflow-hidden p-8">{children}</div>
  </div>
);

const Phase = ({ active }) => {
  const phases = ["Discovery", "Design Concept", "Procurement", "Installation", "Complete"];
  return (
    <div className="flex items-center gap-2 mt-3">
      {phases.map((p, i) => (
        <div key={p} className="flex-1">
          <div className={`h-1.5 rounded-full ${i <= active ? "bg-[#A68A6B]" : "bg-[#EBE5E0]"}`} />
          <p className={`mt-1.5 text-[9px] uppercase tracking-wider ${i === active ? "text-[#A68A6B] font-semibold" : "text-[#9b938b]"}`}>{p}</p>
        </div>
      ))}
    </div>
  );
};

/* ---------- scenes ---------- */
function SceneDashboard({ r }) {
  const cards = [
    { name: "New Canaan", sub: "Coastal Remodel", client: "The Whitmore Family", phase: "Procurement", pct: 62, chk: "7 / 11", photo: PHOTOS.home, hot: true },
    { name: "Beacon Hill Townhouse", sub: "Full Renovation", client: "A. Sullivan", phase: "Design Concept", pct: 34, chk: "3 / 9", photo: PHOTOS.newEngland },
    { name: "Harborview Penthouse", sub: "Furnishing", client: "M. Delacroix", phase: "Installation", pct: 88, chk: "12 / 14", photo: PHOTOS.bedroom },
  ];
  return (
    <Frame>
      <p className="text-[11px] uppercase tracking-[0.25em] text-[#A68A6B]">Your workspace</p>
      <h1 className="font-serif text-4xl text-[#2B2622] mt-1">Projects</h1>
      <p className="text-sm text-[#5C5651] mt-1 mb-5">3 active projects in your studio</p>
      <div className="grid grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.name} className={`bg-white border transition-all duration-500 ${c.hot && r ? "border-[#A68A6B] shadow-lg -translate-y-1" : "border-[#EBE5E0]"}`}>
            <div className="h-28 bg-cover bg-center" style={{ backgroundImage: `url(${c.photo})` }} />
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#A68A6B]"><span className="w-1.5 h-1.5 rounded-full bg-[#A68A6B]" />{c.phase}</span>
                {c.hot && <span className="inline-flex items-center gap-1 bg-[#A68A6B] text-white text-[9px] uppercase tracking-wider px-1.5 py-0.5"><Bell size={9} />2 new</span>}
              </div>
              <h3 className="font-serif text-xl text-[#2B2622] mt-2">{c.name}</h3>
              <p className="text-xs text-[#9b938b]">{c.sub} · {c.client}</p>
              <div className="mt-3">
                <div className="flex justify-between text-[10px] text-[#5C5651] mb-1"><span className="uppercase tracking-wider text-[#A68A6B]">Checklists</span><span>{c.chk} done</span></div>
                <div className="h-1 bg-[#F2EFEB]"><div className="h-full bg-[#A68A6B]" style={{ width: `${c.pct}%` }} /></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function SceneOverview() {
  return (
    <Frame tab="Overview">
      <div className="grid grid-cols-3 gap-6 h-full">
        <div className="col-span-2">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#A68A6B]">Discovery → Install</p>
          <h1 className="font-serif text-4xl text-[#2B2622] mt-1">New Canaan</h1>
          <p className="text-[#5C5651] mt-1">Coastal Remodel · The Whitmore Family</p>
          <Phase active={2} />
          <div className="mt-6 bg-white border border-[#EBE5E0] p-5">
            <div className="flex items-center gap-2 text-[#A68A6B] mb-3"><ListChecks size={16} /><span className="text-[11px] uppercase tracking-wider">Checklists / Assignments</span></div>
            {[["Confirm coastal palette with client", true], ["Order brass sconces (x4)", true], ["Finalize FF&E budget", false], ["Schedule procurement walkthrough", false]].map(([t, d], i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <span className={`w-4 h-4 border flex items-center justify-center ${d ? "bg-[#A68A6B] border-[#A68A6B]" : "border-[#C9BBAE]"}`}>{d && <Check size={11} className="text-white" />}</span>
                <span className={`text-sm ${d ? "line-through text-[#9b938b]" : "text-[#2B2622]"}`}>{t}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-cover bg-center border border-[#EBE5E0]" style={{ backgroundImage: `url(${PHOTOS.home})` }} />
      </div>
    </Frame>
  );
}

function SceneMoodboard({ r }) {
  return (
    <Frame tab="Mood Boards">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl text-[#2B2622]">Coastal Living — Palette & Textures</h2>
        <span className="inline-flex items-center gap-1.5 bg-[#A68A6B] text-white text-[11px] uppercase tracking-wider px-3 py-2"><Plus size={13} /> Add from Pinterest</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-cover bg-center h-40" style={{ backgroundImage: `url(${PHOTOS.living})` }} />
        <div className="bg-cover bg-center h-40" style={{ backgroundImage: `url(${PHOTOS.bedroom})` }} />
        <div className="bg-cover bg-center h-40" style={{ backgroundImage: `url(${PHOTOS.dining})` }} />
        <div className={`bg-cover bg-center h-40 transition-all duration-700 relative ${r ? "opacity-100 scale-100 ring-2 ring-[#A68A6B]" : "opacity-0 scale-90"}`} style={{ backgroundImage: `url(${PHOTOS.newEngland})` }}>
          {r && <span className="absolute bottom-1 left-1 bg-white/90 text-[9px] uppercase tracking-wider px-1.5 py-0.5 text-[#8C7154]">New England living · Pinterest</span>}
        </div>
      </div>
      <p className="text-xs text-[#9b938b] mt-3">Imported from a Pinterest board · New England coastal interiors</p>
    </Frame>
  );
}

function ScenePalette({ r }) {
  const swatches = [
    ["Chantilly Lace", "OC-65", "#F4F2EC"], ["White Sand", "OC-10", "#E8DECB"],
    ["Salted Lake", "1601", "#B7C4C6"], ["Newburg Green", "HC-158", "#3E5A5B"],
    ["Hale Navy", "HC-154", "#3B4A57"], ["Weathered Oak", "—", "#B99C74"],
  ];
  return (
    <Frame tab="Mood Boards">
      <div className="grid grid-cols-3 gap-6 h-full">
        <div className="col-span-1 bg-cover bg-center border border-[#EBE5E0]" style={{ backgroundImage: `url(${PHOTOS.newEngland})` }} />
        <div className="col-span-2">
          <div className="flex items-center gap-2 text-[#A68A6B] mb-2"><Sparkles size={16} /><span className="text-[11px] uppercase tracking-wider">Generated paint palette</span></div>
          <h2 className="font-serif text-2xl text-[#2B2622] mb-4">A calm coastal scheme</h2>
          <div className="grid grid-cols-3 gap-3">
            {swatches.map((s, i) => (
              <div key={i} className={`border border-[#EBE5E0] bg-white transition-all duration-500 ${r ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{ transitionDelay: `${i * 90}ms` }}>
                <div className="h-16" style={{ background: s[2] }} />
                <div className="p-2">
                  <p className="text-xs text-[#2B2622] font-medium leading-tight">{s[0]}</p>
                  <p className="text-[10px] text-[#9b938b]">Benjamin Moore · {s[1]}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Frame>
  );
}

function SceneFFE({ r }) {
  return (
    <Frame tab="FF&E">
      <div className="grid grid-cols-3 gap-6 h-full">
        <div className="col-span-1">
          <div className="bg-white border border-[#EBE5E0] p-2"><div className="h-44 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.sconce})` }} /></div>
          <div className="mt-3 flex items-center gap-2 bg-white border border-[#EBE5E0] px-3 py-2">
            <Search size={14} className="text-[#A68A6B]" />
            <span className="text-xs text-[#5C5651] truncate">visualcomfort.com/brass-sconce-cream</span>
            <span className="ml-auto bg-[#A68A6B] text-white text-[10px] uppercase tracking-wider px-2 py-1">Fetch</span>
          </div>
        </div>
        <div className="col-span-2 bg-white border border-[#EBE5E0] p-5">
          <div className="flex items-center gap-2 text-[#A68A6B] mb-3"><Sparkles size={15} /><span className="text-[11px] uppercase tracking-wider">Smart import</span></div>
          {[["Item", "Anette Brass Sconce, Cream Linen Shade"], ["Category", "Lighting · Wall"], ["Room", "Living Room"], ["Vendor", "Visual Comfort & Co."], ["Quantity", "4"], ["Unit price", "$429.00"], ["Total", "$1,716.00"]].map(([k, v], i) => (
            <div key={i} className={`flex justify-between py-2 border-b border-[#F2EFEB] transition-all duration-500 ${r ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: `${i * 70}ms` }}>
              <span className="text-[11px] uppercase tracking-wider text-[#9b938b]">{k}</span>
              <span className={`text-sm ${k === "Total" ? "font-serif text-lg text-[#2B2622]" : "text-[#2B2622]"}`}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

function SceneCalendar({ r }) {
  return (
    <Frame tab="Calendar">
      <div className="grid grid-cols-2 gap-6 h-full">
        <div>
          <h2 className="font-serif text-2xl text-[#2B2622] mb-3">June 2026</h2>
          <div className="grid grid-cols-7 gap-1 text-center">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-[10px] uppercase text-[#9b938b] py-1">{d}</div>)}
            {Array.from({ length: 30 }, (_, i) => i + 1).map((d) => (
              <div key={d} className={`py-1.5 text-xs ${[12, 18, 24].includes(d) ? "bg-[#A68A6B] text-white" : "text-[#5C5651]"}`}>{d}</div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 text-[#A68A6B] mb-3"><CalendarClock size={16} /><span className="text-[11px] uppercase tracking-wider">Upcoming tasks</span></div>
          {[["Site walkthrough — procurement", "Jun 12", true], ["Sconce install approval", "Jun 18", false], ["Client budget review", "Jun 24", false]].map(([t, d, isNew], i) => (
            <div key={i} className={`flex items-center gap-3 bg-white border border-[#EBE5E0] px-4 py-3 mb-2 transition-all duration-500 ${(isNew && !r) ? "opacity-0 -translate-x-3" : "opacity-100"}`}>
              <div className="text-center w-12"><p className="font-serif text-xl text-[#A68A6B] leading-none">{String(d).split(" ")[1]}</p><p className="text-[9px] uppercase text-[#9b938b]">{String(d).split(" ")[0]}</p></div>
              <p className="text-sm text-[#2B2622]">{t}{isNew && <span className="ml-2 text-[9px] uppercase tracking-wider bg-[#F5EAD9] text-[#8C7154] px-1.5 py-0.5">new</span>}</p>
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
}

function SceneNotes({ r }) {
  return (
    <Frame tab="Notes">
      <div className="max-w-2xl">
        <div className="bg-white border border-[#EBE5E0] p-5">
          <textarea readOnly className="w-full text-sm text-[#2B2622] resize-none border-0 focus:ring-0 outline-none" rows={3}
            value={"Team & Clara — let's hold a budget review this Thursday at 2pm. The coastal palette is approved and procurement is underway (sconces ordered). We're on track for installation."} />
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F2EFEB]">
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#A68A6B]"><span className="w-2 h-2 bg-[#506B52]" /> To: Everyone · team + client</span>
            <span className={`inline-flex items-center gap-1.5 text-white text-[11px] uppercase tracking-wider px-4 py-2 transition-colors ${r ? "bg-[#506B52]" : "bg-[#A68A6B]"}`}>
              {r ? <><Check size={13} /> Sent</> : <><Send size={13} /> Send</>}
            </span>
          </div>
        </div>
        <p className="text-xs text-[#9b938b] mt-2">Category: Budget · notifies all recipients by email</p>
      </div>
    </Frame>
  );
}

function SceneBudget() {
  const rows = [["Furnishings", 42000, 62], ["Lighting", 12000, 48], ["Textiles & Rugs", 9500, 30], ["Millwork", 18000, 74], ["Contingency", 6000, 12]];
  return (
    <Frame tab="Budget">
      <div className="flex items-end justify-between mb-4">
        <div><p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Allocated</p><h2 className="font-serif text-3xl text-[#2B2622]">$87,500</h2></div>
        <div className="text-right"><p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Spent to date</p><p className="font-serif text-2xl text-[#8C7154]">$41,320</p></div>
      </div>
      <div className="bg-white border border-[#EBE5E0] divide-y divide-[#F2EFEB]">
        {rows.map(([cat, amt, pct], i) => (
          <div key={i} className="px-5 py-3">
            <div className="flex justify-between text-sm mb-1.5"><span className="text-[#2B2622]">{cat}</span><span className="text-[#5C5651]">${amt.toLocaleString()}</span></div>
            <div className="h-1.5 bg-[#F2EFEB]"><div className="h-full bg-[#A68A6B]" style={{ width: `${pct}%` }} /></div>
          </div>
        ))}
      </div>
    </Frame>
  );
}

function SceneAssistant({ r }) {
  return (
    <Frame tab="AI Assistant">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-end mb-3"><div className="bg-[#A68A6B] text-white text-sm px-4 py-3 max-w-[80%]">When are the next estate sales and flea markets where I could source an antique dining set for New Canaan?</div></div>
        <div className={`flex items-start gap-2 transition-all duration-500 ${r ? "opacity-100" : "opacity-0"}`}>
          <div className="bg-[#F2EFEB] text-[#2B2622] text-sm px-4 py-3 leading-relaxed max-w-[80%]">
            Great options for coastal antiques near New Canaan:<br />
            • <b>Elephant's Trunk Flea Market</b>, New Milford — Sundays through the season.<br />
            • <b>Stamford Antiques &amp; Estate Sale</b> — the third weekend of the month.<br />
            • <b>Woodbury Antiques Weekend</b> — a strong source for farmhouse dining tables.<br />
            Look for a solid oak or walnut trestle table to complement the palette.
          </div>
          <div className={`flex flex-col gap-1 transition-opacity duration-300 ${r ? "opacity-100" : "opacity-0"}`}>
            <span className={`p-1.5 border ${r ? "border-[#506B52] text-[#506B52]" : "border-[#EBE5E0] text-[#C9BBAE]"}`}>{r ? <Check size={14} /> : <Plus size={14} />}</span>
          </div>
        </div>
        {r && <p className="text-[11px] text-[#506B52] mt-2 ml-1">✓ Saved to Notes</p>}
      </div>
    </Frame>
  );
}

function SceneClosing() {
  return (
    <div className="absolute inset-0 bg-[#2B2622] flex flex-col items-center justify-center text-center">
      <div className="absolute inset-0 opacity-15 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.home})` }} />
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#C9A87C] mb-3">For interior designers</p>
        <h1 className="font-serif text-6xl text-white tracking-wide">WorkRoom</h1>
        <p className="text-[#D9CBBF] mt-4 text-lg font-serif italic">Design, managed beautifully.</p>
      </div>
    </div>
  );
}

const SCENE_COMPONENTS = [SceneDashboard, SceneOverview, SceneMoodboard, ScenePalette, SceneFFE, SceneCalendar, SceneNotes, SceneBudget, SceneAssistant, SceneClosing];

/* ---------- reel ---------- */
export default function PromoReel({ onClose }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const audioRef = useRef(null);
  const timers = useRef([]);
  const { recording, start: recStart, stop: recStop } = useReelRecorder("workroom-promo-reel");
  const recMode = useRef(false);

  useEffect(() => {
    api.post("/admin/promo-voiceover", { scenes: SCENES.map((s) => s.narration), voice: "sage" })
      .then((r) => setClips(r.data.clips || []))
      .catch(() => setClips([]))
      .finally(() => setLoading(false));
  }, []);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const runScene = useCallback((i) => {
    clearTimers();
    setRevealed(false);
    timers.current.push(setTimeout(() => setRevealed(true), REVEAL_MS));
    const url = clips[i];
    const advance = () => setIdx((p) => (p === i ? i + 1 : p));
    if (url && audioRef.current) {
      audioRef.current.src = fileSrc(url);
      audioRef.current.onended = advance;
      audioRef.current.play().catch(() => { timers.current.push(setTimeout(advance, FALLBACK_MS)); });
    } else {
      timers.current.push(setTimeout(advance, FALLBACK_MS));
    }
  }, [clips]);

  useEffect(() => {
    if (!started) return;
    if (idx >= SCENES.length) {
      if (recMode.current) { recMode.current = false; recStop(); setStarted(false); setIdx(0); }
      else onClose();
      return;
    }
    runScene(idx);
    return clearTimers;
    // eslint-disable-next-line
  }, [idx, started]);

  const start = () => {
    setStarted(true);
    setIdx(0);
  };
  const recordAndPlay = async () => {
    const ok = await recStart();
    if (!ok) return;
    recMode.current = true;
    setStarted(true);
    setIdx(0);
  };
  const stopRecording = () => { recMode.current = false; recStop(); clearTimers(); if (audioRef.current) audioRef.current.pause(); setStarted(false); setIdx(0); };
  const restart = () => { clearTimers(); if (audioRef.current) audioRef.current.pause(); setIdx(0); setStarted(true); };

  const Scene = SCENE_COMPONENTS[Math.min(idx, SCENES.length - 1)];
  const scene = SCENES[Math.min(idx, SCENES.length - 1)];

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" data-testid="promo-reel">
      <audio ref={audioRef} className="hidden" />
      <button data-testid="promo-close" onClick={onClose}
        className="absolute top-5 right-6 text-white/70 hover:text-white z-20"><X size={26} /></button>

      <div className="w-full max-w-6xl">
        <div className="relative w-full aspect-video bg-[#F9F8F6] overflow-hidden shadow-2xl">
          {!started ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center bg-[#2B2622]">
              <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.home})` }} />
              <div className="relative">
                <h2 className="font-serif text-5xl text-white">WorkRoom Promo Reel</h2>
                <p className="text-[#D9CBBF] mt-2">A 60-second designer walkthrough · New Canaan coastal remodel</p>
                <button data-testid="promo-play" onClick={start} disabled={loading}
                  className="mt-6 inline-flex items-center gap-2 bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-sm px-8 py-4 transition-colors disabled:opacity-60">
                  <Play size={18} /> {loading ? "Preparing voiceover…" : "Play reel"}
                </button>
                <button data-testid="promo-record" onClick={recordAndPlay} disabled={loading}
                  className="mt-3 ml-3 inline-flex items-center gap-2 border border-[#A68A6B] text-white/90 hover:bg-[#A68A6B] uppercase tracking-wider text-sm px-6 py-4 transition-colors disabled:opacity-60">
                  <Video size={18} /> Record & download
                </button>
                <p className="text-[11px] text-[#9b938b] mt-4">Screen-record, or "Record & download" to save a video with voiceover (pick this tab & tick "share tab audio" when prompted).</p>
              </div>
            </div>
          ) : (
            <>
              <div key={idx} className="absolute inset-0 animate-[fadeIn_0.6s_ease]"><Scene r={revealed} /></div>
              {idx < SCENES.length - 1 && (
                <div className="absolute z-10 transition-all duration-[1600ms] ease-out"
                  style={{ left: `${scene.cursor.x}%`, top: `${scene.cursor.y}%`, transform: "translate(-50%,-50%)" }}>
                  <MousePointer2 size={30} className="text-[#2B2622] drop-shadow-[0_2px_3px_rgba(255,255,255,0.9)] fill-white" />
                  {revealed && <span className="absolute -inset-2 rounded-full border-2 border-[#A68A6B] animate-ping" />}
                </div>
              )}
              {scene.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-8 py-5">
                  <p className="text-white/90 font-serif text-lg">{scene.caption}</p>
                </div>
              )}
              <div className="absolute top-0 left-0 right-0 h-1 bg-white/20">
                <div className="h-full bg-[#A68A6B] transition-all duration-500" style={{ width: `${((idx + 1) / SCENES.length) * 100}%` }} />
              </div>
            </>
          )}
        </div>

        {started && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <button data-testid="promo-restart" onClick={restart} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-wider">
              <RotateCcw size={15} /> Restart
            </button>
            {recording && (
              <button data-testid="promo-stop-record" onClick={stopRecording} className="inline-flex items-center gap-2 text-[#E7A0A0] hover:text-white text-xs uppercase tracking-wider">
                <Square size={13} className="fill-current" /> Stop & save
              </button>
            )}
            <span className="text-white/40 text-xs">Scene {Math.min(idx + 1, SCENES.length)} / {SCENES.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}
